import { Checkbox, Collapse, Flex } from "antd";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { useEditProductContext } from "../../../../contexts/edit-product-context/useEditProductContext";
import styled from "styled-components";
import { ProductBaseVariant } from "./ProductBaseVariant";
import { useAdditionsTour } from "../../../../contexts/additions-tour-context/useAdditionsTour";

const StyledCollapse = styled(Collapse)`
  .ant-collapse-content-box {
    padding: 0 !important;
  }

  .ant-collapse-content {
    border-top: none !important;
  }

  margin-bottom: 12px;

  .ant-collapse-header {
    flex-direction: row-reverse !important;
    padding: 12px 16px !important;
    align-items: center !important;
  }

  .ant-collapse-expand-icon {
    padding-inline-start: 12px !important;
    padding-inline-end: 0 !important;
    display: flex !important;
    align-items: center !important;
  }

  .ant-checkbox-inner {
    width: 18px !important;
    height: 18px !important;
  }

  .ant-checkbox-checked .ant-checkbox-inner::after {
    width: 6px !important;
    height: 10px !important;
  }
`;

const CategoryTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0px;
`;

const CategoryName = styled.span`
  font-size: 14px;
  font-weight: 500;
`;

const CategoryType = styled.span`
  font-size: 12px;
  color: var(--color-text-secondary);
  font-weight: 400;
`;

// Funkcja pomocnicza do generowania opisu kategorii
const getCategoryDescription = (category: any, t: TFunction): string => {
  const typeLabels: Record<string, string> = {
    SELECT: t("extensionConfiguratorCategory.typeSelect"),
    MULTISELECT: t("extensionConfiguratorCategory.typeMultiselect"),
    QUANTITIES: t("extensionConfiguratorCategory.typeQuantities"),
  };

  const typeName = typeLabels[category.type] || category.type;
  const min = category.minimumRequiredElements || 0;
  const max = category.maximumRequiredElements || 0;

  if (category.type === 'SELECT') {
    return min > 0
      ? t("extensionConfiguratorCategory.requiredSuffix", { typeName })
      : t("extensionConfiguratorCategory.optionalSuffix", { typeName });
  }

  if (max === 0) {
    return `${typeName} ${min}-${max}`;
  }

  return `${typeName} ${min}-${max}`;
};

export const ExtensionCategory: React.FC<any> = ({
  category,
  options,
  hasError = false,
  isExpanded = false,
  onExpandChange,
  isFirstForTour = false,
}) => {
  const { t } = useTranslation();
  const {
    setUnsavedChanges,
    productBaseVariants,
    extensionsConfig,
    setExtensionsConfig,
  } = useEditProductContext();
  const { isTourOpen, activeTour, tourStep, advanceStep } = useAdditionsTour();

  const checked = category?.extensions?.reduce((acc: any, element: any) => {
    const config: any = extensionsConfig;
    productBaseVariants.forEach((productBaseVariant) => {
      if (!config[productBaseVariant.key]?.[element.id]?.name) {
        acc = false;
      }
    });
    return acc;
  }, true);

  const indeterminateArray: boolean[] = [];

  category?.extensions?.forEach((element: any) => {
    const config: any = extensionsConfig;
    productBaseVariants.forEach((productBaseVariant) => {
      if (!config[productBaseVariant.key]?.[element.id]?.name) {
        indeterminateArray.push(true);
      } else {
        indeterminateArray.push(false);
      }
    });
  });

  const indeterminate =
    !indeterminateArray.every((e) => e === true) &&
    !indeterminateArray.every((e) => e === false);

  return (
    <StyledCollapse
      activeKey={isExpanded ? ["1"] : []}
      onChange={(keys) => {
        const expanded = keys.includes("1");
        onExpandChange?.(expanded);
      }}
      items={[
        {
          key: "1",
          label: (
            <Flex align="center" gap={12}>
              <Checkbox
                onClick={(e) => e.stopPropagation()}
                indeterminate={indeterminate}
                checked={checked}
                onChange={(val) => {
                  const config: any = extensionsConfig;

                  productBaseVariants.forEach((productBaseVariant) => {
                    if (val.target.checked) {
                      const object = category?.extensions?.reduce(
                        (acc: any, element: any) => {
                          let name = options[0]?.value;
                          if (
                            config[productBaseVariant.key]?.[element.id]
                              ?.name
                          ) {
                            name =
                              config[productBaseVariant.key]?.[element.id]
                                ?.name;
                          }
                          return {
                            ...acc,
                            [`${element.id}`]: { name: name },
                          };
                        },
                        {}
                      );

                      config[productBaseVariant.key] = {
                        ...config[productBaseVariant.key],
                        ...object,
                      };
                    } else {
                      category?.extensions?.forEach((element: any) => {
                        delete config[productBaseVariant.key][element.id];
                      });
                    }
                  });

                  setExtensionsConfig({ ...config });
                  setUnsavedChanges(true);

                  // Advance attach-additions tour when first category checkbox is clicked
                  if (isFirstForTour && isTourOpen && activeTour === "attach-additions" && (tourStep === 4 || tourStep === 5)) {
                    setTimeout(() => advanceStep(tourStep), 400);
                  }
                }}
              />
              <CategoryTitle>
                <CategoryName>{category.name}</CategoryName>
                <CategoryType>{getCategoryDescription(category, t)}</CategoryType>
              </CategoryTitle>
            </Flex>
          ),
          children: (
            <>
              {options.length > 1 ? (
                // Jeśli grupa dodatków ma więcej niż 1 wariant cenowy, pokazuj dla każdego wariantu produktu
                productBaseVariants
                  .sort((a: any, b: any) => a.id - b.id)
                  .map((e) => {
                    return (
                      <ProductBaseVariant
                        key={e.id}
                        productBaseVariant={e}
                        category={category}
                        options={options}
                        hasMultipleProductVariants={productBaseVariants.length > 1}
                        hasError={hasError}
                      />
                    );
                  })
              ) : (
                // Jeśli grupa dodatków ma tylko 1 wariant cenowy, pokazuj tylko raz
                <ProductBaseVariant
                  productBaseVariant={productBaseVariants[0]}
                  category={category}
                  options={options}
                  hasMultipleProductVariants={false}
                  hasError={false}
                />
              )}
            </>
          ),
        },
      ]}
    />
  );
};
